import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp as ExitToAppIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';

interface LegrandAppBarProps {
  /** Titre affiché dans la barre sur desktop (ex. "LEGRAND - Gestion des Demandes"). */
  title: string;
  /** Nom d'utilisateur affiché dans le menu compte. */
  username?: string;
  /**
   * Affiche le bouton "Tableau de bord" sur desktop. `false` sur le Dashboard
   * lui-même (où ce bouton n'existait pas). Défaut: true.
   */
  showDashboardButton?: boolean;
}

const NAV_LINKS = [
  { to: '/legrand/dashboard', label: 'Tableau de bord' },
  { to: '/legrand/demandes', label: 'Gestion des demandes' },
  { to: '/legrand/adresses', label: 'Adresses' },
  { to: '/legrand/emails', label: 'Emails' },
];

/**
 * Barre de navigation Legrand factorisée.
 *
 * - Desktop (>= 600px) : rendu STRICTEMENT identique aux AppBar inline qui
 *   existaient dans chaque écran Legrand (titre + bouton "Tableau de bord"
 *   optionnel + menu compte).
 * - Mobile (< 600px) : un menu hamburger ouvre un Drawer latéral listant les
 *   sections et la déconnexion, et le titre est raccourci pour ne pas déborder.
 */
const LegrandAppBar: React.FC<LegrandAppBarProps> = ({
  title,
  username,
  showDashboardButton = true,
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token_legrand');
    localStorage.removeItem('user_legrand');
    localStorage.removeItem('societe');
    navigate('/');
  };

  const goTo = (to: string) => {
    setDrawerOpen(false);
    navigate(to);
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
      <Toolbar>
        {isMobile ? (
          <>
            <IconButton
              color="inherit"
              edge="start"
              aria-label="ouvrir le menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }} noWrap>
              LEGRAND
            </Typography>

            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              <Box sx={{ width: 260 }} role="presentation">
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    LEGRAND
                  </Typography>
                  {username && (
                    <Typography variant="body2" color="text.secondary">
                      {username}
                    </Typography>
                  )}
                </Box>
                <Divider />
                <List>
                  {NAV_LINKS.map((link) => (
                    <ListItemButton key={link.to} onClick={() => goTo(link.to)}>
                      <ListItemText primary={link.label} />
                    </ListItemButton>
                  ))}
                  <Divider />
                  <ListItemButton onClick={handleLogout}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ExitToAppIcon />
                    </ListItemIcon>
                    <ListItemText primary="Déconnexion" />
                  </ListItemButton>
                </List>
              </Box>
            </Drawer>
          </>
        ) : (
          <>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {title}
            </Typography>
            {showDashboardButton && (
              <Button
                color="inherit"
                startIcon={<DashboardIcon />}
                onClick={() => navigate('/legrand/dashboard')}
                sx={{ mr: 2 }}
              >
                Tableau de bord
              </Button>
            )}
            <IconButton
              size="large"
              aria-label="compte utilisateur"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Typography>{username}</Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToAppIcon sx={{ mr: 1 }} />
                Déconnexion
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default LegrandAppBar;
